import { type models } from "jiffy-api";
import { RadioGroup, Radio } from "@design-lab/shared";
import { Check } from "lucide-react";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { useWatch } from "react-hook-form";
import type { ArtworkForm } from "@design-lab/shared/types";
import type { DesignLabPlacementKey } from "@design-lab/entities/pinning";
import { PrintPerfectExplanatoryTooltip } from "./PrintPerfectExplanatoryTooltip";
import { PrintPerfect } from "./PrintPerfect";
import { RequestDesignChanges } from "./RequestDesignChanges";
import { ActiveSessionIndicator } from "./ActiveSessionIndicator";

export type QualityTabsProps = {
  value: models.cart.madeAdd.EnhancementTier | undefined;
  onChange: (value: models.cart.madeAdd.EnhancementTier) => void;
  canRemoveBackground?: boolean;
  canEditCutoutModal?: boolean;
  showRefineArtwork?: boolean;
  showPrintPerfectTooltip?: boolean;
  tooltipTitle?: string;
  tooltipFeatures?: string[];
  tooltipWidth?: string;
  // PrintPerfect props
  removeBackground: boolean;
  jiffyDesignServices: boolean;
  editImageManual: boolean;
  instructionsError?: string | undefined;
  placementSide?: "front" | "back";
  onRemoveBackgroundChange: (checked: boolean) => void;
  onJiffyDesignServicesChange: (checked: boolean) => void;
  onEnhancementRequestNoteChange: (value: string) => void;
  onRefineArtworkClick?: () => void;
  onResetManualEdit?: () => void;
  enhancementRequestNoteField: any;
  removeBackgroundLens?: any;
};

const DEFAULT_PRINT_PERFECT_FEATURES = [
  "Enhances color & detail",
  "Removes blur & pixelation",
  "Vectorizes and resizes your design"
];

export function QualityTabs({
  value,
  onChange,
  canRemoveBackground = true,
  canEditCutoutModal = true,
  showRefineArtwork = true,
  showPrintPerfectTooltip = true,
  tooltipTitle = "What do you get with PrintPerfect?",
  tooltipFeatures = DEFAULT_PRINT_PERFECT_FEATURES,
  tooltipWidth = "456px",
  // PrintPerfect props
  removeBackground,
  jiffyDesignServices,
  editImageManual,
  instructionsError,
  placementSide,
  onRemoveBackgroundChange,
  onJiffyDesignServicesChange,
  onEnhancementRequestNoteChange,
  onRefineArtworkClick,
  onResetManualEdit,
  enhancementRequestNoteField,
  removeBackgroundLens
}: QualityTabsProps) {
  const { form } = useArtworkForm();
  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  const selectedFrontPlacement = artworkForm?.selectedFrontPlacement as
    | DesignLabPlacementKey
    | undefined;
  let placementKey: DesignLabPlacementKey = "front";
  if (placementSide === "back") {
    placementKey = "back";
  } else if (selectedFrontPlacement === "frontLeftChest") {
    placementKey = "frontLeftChest";
  }

  const handleTabChange = (newValue: string) => {
    onChange(newValue as models.cart.madeAdd.EnhancementTier);
  };

  return (
    <RadioGroup
      data-testid="print-quality-radio-group"
      value={value || "full_enhancements"}
      onChange={handleTabChange}
      className="gap-4"
    >
      <ActiveSessionIndicator
        placementKey={placementKey}
        placementSide={placementSide || "front"}
      />

      <RequestDesignChanges
        placementSide={placementSide || "front"}
        currentValue={value}
        removeBackground={removeBackground}
        canRemoveBackground={canRemoveBackground}
        editImageManual={editImageManual}
      />

      <div className="space-y-2 border-gray-600 lg:rounded-lg lg:border lg:p-3">
        <label
          data-testid="print-perfect-label"
          className="flex items-center gap-2"
          htmlFor="full_enhancements-radio"
        >
          <Radio
            data-testid="print-perfect-radio"
            id="full_enhancements-radio"
            value="full_enhancements"
          />
          <div className="flex w-full items-center justify-start gap-1">
            <span className="font-medium">PrintPerfect</span>
            <span className="hidden text-sm font-medium text-green-700 lg:block">Free</span>
            <span className="text-background block rounded-sm bg-pink-500 px-2 py-1 text-xs lg:hidden">
              Free
            </span>
            {showPrintPerfectTooltip && (
              <div className="flex flex-1 justify-end">
                <PrintPerfectExplanatoryTooltip setWidth={tooltipWidth}>
                  <div className="mb-6 space-y-4 lg:mb-0 lg:p-4">
                    <h3 className="text-lg font-bold">{tooltipTitle}</h3>
                    {tooltipFeatures.map(feature => (
                      <div
                        className="flex items-center gap-2 text-lg font-normal lg:font-medium"
                        key={feature}
                      >
                        <Check className="stroke-green-500" />
                        <p>{feature}</p>
                      </div>
                    ))}
                  </div>
                </PrintPerfectExplanatoryTooltip>
              </div>
            )}
          </div>
        </label>

        <PrintPerfect
          canRemoveBackground={canRemoveBackground}
          showRefineArtwork={showRefineArtwork}
          canEditCutoutModal={canEditCutoutModal}
          removeBackground={removeBackground}
          jiffyDesignServices={jiffyDesignServices}
          editImageManual={editImageManual}
          instructionsError={instructionsError}
          placementSide={placementSide || "front"}
          onRemoveBackgroundChange={onRemoveBackgroundChange}
          onJiffyDesignServicesChange={onJiffyDesignServicesChange}
          onEnhancementRequestNoteChange={onEnhancementRequestNoteChange}
          onRefineArtworkClick={onRefineArtworkClick}
          onResetManualEdit={onResetManualEdit}
          enhancementRequestNoteField={enhancementRequestNoteField}
          removeBackgroundLens={removeBackgroundLens}
        />
      </div>

      <hr className="lg:hidden" />

      <div className="space-y-2 lg:rounded-lg lg:border lg:p-3">
        <label
          data-testid="print-as-is-label"
          className="flex items-center gap-2"
          htmlFor="print_original-radio"
        >
          <Radio data-testid="print-as-is-radio" id="print_original-radio" value="print_original" />
          <span className="font-medium">Print As Is</span>
        </label>
      </div>
    </RadioGroup>
  );
}
